import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header.jsx';
import { Footer } from '../components/Blocks.jsx';

// Playful, self-deprecating 404. On-brand: warm, conversational, slightly
// Welsh, doesn't pretend to be a brand-new page. The numbers are big
// because the joke needs the numbers to land first.
//
// Suggestions are routes most people 404 their way looking for.

const SUGGESTIONS = [
  { label: 'Pricing', to: '/pricing', desc: "How much it costs (spoiler: less than you'd think)" },
  { label: 'Pick a language', to: '/#sample', desc: 'Hear three minutes of the Method, in your chosen language' },
  { label: 'About us', to: '/about', desc: 'Who we are and why we keep doing this' },
  { label: 'Contact', to: '/contact', desc: "Tell us what you were looking for and we'll point you at it" },
];

const APOLOGIES = [
  // Light, slightly self-deprecating, not too try-hard. One picked at random
  // per mount so refreshing /broken-url cycles through.
  "We've all been there.",
  "Don't blame yourself.",
  "Our fault, almost certainly.",
  'A page may have wandered off.',
  'The internet is a wide and confusing place.',
];

const minimalT = {
  navWelshResources: 'Welsh Resources',
  navAboutUs: 'About us',
  navContact: 'Contact',
  startLearning: 'Start Learning',
  footerCta: 'Start your language learning journey today.',
  footerCol1: 'What we do',
  footerCol2: 'SaySomethingin Learners',
  footerLegal: '© 2026 SaySomethingIn Cyf. Glaslyn, Ffordd y Parc, Bangor, Gwynedd LL57 4FE, Wales.',
};

export default function NotFound() {
  // Pick once per mount so refreshing the same broken URL cycles the line.
  const [apology] = useState(() => APOLOGIES[Math.floor(Math.random() * APOLOGIES.length)]);

  return (
    <div data-screen-label="Not found">
      <Header t={minimalT}/>
      <main style={pageStyles.wrap}>
        <div style={pageStyles.numberRow} aria-hidden="true">
          <span style={pageStyles.digit}>4</span>
          <span style={pageStyles.zero}>
            <span style={pageStyles.zeroInner}/>
          </span>
          <span style={pageStyles.digit}>4</span>
        </div>

        <div style={pageStyles.kicker}>Croeso i nunlle.</div>
        <h1 style={pageStyles.h1}>Welcome to nowhere.</h1>
        <p style={pageStyles.lede}>
          Whatever you were looking for, it's not here. {apology} Have a snoop
          around — we promise the rest of the site has more in it than this page does.
        </p>

        <ul style={pageStyles.suggestions}>
          {SUGGESTIONS.map(s => (
            <li key={s.to} style={pageStyles.suggestion}>
              <Link to={s.to} style={pageStyles.suggestionLink}>
                <span style={pageStyles.suggestionLabel}>{s.label}</span>
                <span style={pageStyles.suggestionDesc}>{s.desc}</span>
                <span style={pageStyles.suggestionArrow} aria-hidden="true">→</span>
              </Link>
            </li>
          ))}
        </ul>

        <div style={pageStyles.aside}>
          <em>If something is genuinely broken</em>, the team reads every message that comes through{' '}
          <Link to="/contact" style={pageStyles.contactLink}>contact</Link>. Tell us what you were
          looking for and we'll either fix the link or apologise more thoroughly.
        </div>
      </main>
      <Footer t={minimalT} hideCta={true}/>
    </div>
  );
}

const pageStyles = {
  wrap: {
    background: 'var(--offWhite, #FDF5F5)',
    padding: '72px 32px 96px',
    minHeight: 'calc(100vh - 240px)',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
  },
  numberRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 8, marginBottom: 32, color: 'var(--brandRed)',
    fontFamily: 'var(--font-display)',
    lineHeight: 1, letterSpacing: '-.04em',
    userSelect: 'none',
  },
  digit: {
    fontSize: 'clamp(96px, 18vw, 192px)',
    fontWeight: 500,
  },
  zero: {
    width: 'clamp(72px, 14vw, 144px)',
    height: 'clamp(96px, 18vw, 192px)',
    borderRadius: '50%',
    border: 'clamp(8px, 1.6vw, 18px) solid var(--brandRed)',
    background: 'transparent',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    position: 'relative',
  },
  zeroInner: {
    width: '32%',
    height: '32%',
    borderRadius: '50%',
    background: 'var(--gold)',
    boxShadow: '0 0 0 2px var(--brandRed)',
  },
  kicker: {
    fontSize: 13, letterSpacing: '.16em', textTransform: 'uppercase',
    color: 'var(--brandRed)', fontWeight: 600,
    marginBottom: 12,
    fontStyle: 'italic',
  },
  h1: {
    fontFamily: 'var(--font-display)', fontSize: 'clamp(40px, 6vw, 64px)',
    lineHeight: 1.05, letterSpacing: '-.02em',
    margin: '0 0 18px', textAlign: 'center', textWrap: 'balance',
  },
  lede: {
    fontSize: 17, color: 'var(--grey)', lineHeight: 1.55,
    margin: '0 auto 40px', maxWidth: 580, textAlign: 'center', textWrap: 'pretty',
  },
  suggestions: {
    listStyle: 'none', margin: '0 0 32px', padding: 0,
    width: '100%', maxWidth: 720,
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12,
  },
  suggestion: {},
  suggestionLink: {
    display: 'flex', flexDirection: 'column', gap: 4,
    background: '#fff', border: '1px solid var(--border)', borderRadius: 12,
    padding: '18px 20px', position: 'relative',
    textDecoration: 'none', color: 'var(--fg-1)',
    transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
  },
  suggestionLabel: {
    fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 500,
    color: 'var(--brandRed)',
  },
  suggestionDesc: {
    fontSize: 13.5, color: 'var(--grey)', lineHeight: 1.45,
  },
  suggestionArrow: {
    position: 'absolute', top: 18, right: 20,
    color: 'var(--brandRed)', fontSize: 18,
    opacity: .65,
  },
  aside: {
    fontSize: 13.5, color: 'var(--grey)', lineHeight: 1.6,
    maxWidth: 580, textAlign: 'center', textWrap: 'pretty',
    paddingTop: 20, borderTop: '1px solid var(--border)',
  },
  contactLink: {
    color: 'var(--brandRed)', fontWeight: 500, textDecoration: 'underline',
  },
};
